import { useState } from "react";
import type { ResolvedBracketGame } from "@alpha/engine";
import { api, apiErrorMessage } from "../lib/api";
import { useAdminMutation } from "../lib/adminData";
import { trackGameScored } from "../lib/analytics";
import { cx } from "../lib/cx";
import { gameTime } from "../lib/format";
import { venueLabel } from "../lib/gameLocation";
import { isGameLive } from "../lib/liveStatus";
import { downstreamFinishedGame, forfeitLine, gameSides, type Side } from "../lib/scoreCard";
import type { Caps } from "../lib/tournamentPhases";
import type { Bundle, Game } from "../lib/types";
import { Button, Card, GameStatusBadge, Input } from "./ui";

/**
 * One game, scored with a thumb (console spec §5.5). Shared by Scores and
 * Playoffs so a final is entered the same way from either page.
 *
 * Three modes. `edit` is a scheduled game, or a decided one after Change:
 * steppers, Final, and the second row. `forfeit` swaps that row for "Who
 * forfeited?". `view` is a decided game: the result and the way to change it.
 * Scores are local until Final — the API stores a score only on a final game,
 * so there is no half-saved state to represent.
 *
 * The bracket rules live here, not on the page, because Playoffs renders the
 * same card: no Cancel game (a no-show is a forfeit, which advances the other
 * side, whereas a canceled bracket game leaves "Winner Gm N" unresolved for
 * ever); no ties; and no changing a result a later game was played off.
 */
export function ScoreCard({
  tid,
  bundle,
  game,
  resolved,
  caps,
  now,
}: {
  tid: string;
  bundle: Bundle;
  game: Game;
  /** The resolver's view of a bracket game; a pool game passes none. */
  resolved?: ResolvedBracketGame;
  caps: Caps;
  /** The shell's clock reading, for the Live badge. */
  now: Date;
}) {
  const sides = gameSides(bundle, game, resolved);
  const bracket = game.type === "bracket";
  const lockedBy = bracket ? downstreamFinishedGame(bundle, game) : null;
  const decided = game.status !== "scheduled";

  // `view` only means something for a decided game. A card asks for it the
  // moment Final resolves, but the bundle that says the game IS final arrives
  // a refetch later — until then the steppers stay, rather than a result card
  // with nothing on it.
  const [wanted, setMode] = useState<"edit" | "forfeit" | "view">(decided ? "view" : "edit");
  const mode = wanted === "view" && !decided ? "edit" : wanted;
  const [home, setHome] = useState(String(game.homeScore ?? 0));
  const [away, setAway] = useState(String(game.awayScore ?? 0));
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  const put = (body: Record<string, unknown>, then: () => void) =>
    api
      .put(`/admin/tournaments/${tid}/games/${game.id}/score`, body)
      .then(() => {
        setError(undefined);
        then();
        refresh.mutate();
      })
      .catch((e) => setError(apiErrorMessage(e, "Could not save this game.")));

  const final = () =>
    void put({ status: "final", homeScore: Number(home), awayScore: Number(away) }, () => {
      trackGameScored();
      setMode("view");
    });
  const forfeit = (teamId: string) => void put({ status: "forfeit", forfeitTeamId: teamId }, () => setMode("view"));
  const cancel = () => void put({ status: "canceled" }, () => setMode("view"));
  const reinstate = () => void put({ status: "scheduled" }, () => setMode("edit"));
  const change = () => {
    // A forfeit stores no score, so Change opens at 0–0; a final reopens on
    // what was entered.
    setHome(String(game.homeScore ?? 0));
    setAway(String(game.awayScore ?? 0));
    setMode("edit");
  };

  const tied = Number(home) === Number(away);
  const canFinal = !sides.unresolved && !(bracket && tied);
  const unresolvedText = sides.unresolved
    ? bracket
      ? "Waiting on earlier results."
      : caps.schedule
        ? "Both teams are set under Schedule."
        : "An organizer sets both teams."
    : null;
  const disabledText = unresolvedText ?? (bracket && tied ? "Bracket games can’t end tied" : null);

  const time = gameTime(game.scheduledAt, bundle.tournament.timezone);
  const place = venueLabel(bundle.venues, game);
  const showScores = game.status === "final";
  const homeWin = showScores && (game.homeScore ?? 0) > (game.awayScore ?? 0);
  const awayWin = showScores && (game.awayScore ?? 0) > (game.homeScore ?? 0);

  return (
    <Card className={cx("p-4", game.status === "canceled" && "bg-slate-50/60")}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-base font-semibold tabular-nums text-navy-950">
          {time || "Time TBD"}
        </span>
        <GameStatusBadge status={game.status} live={isGameLive(game, bundle.tournament.config, now.getTime())} />
      </div>
      <p className="mt-0.5 truncate text-xs text-slate-500">
        {[place, sides.context].filter(Boolean).join(" · ")}
      </p>

      <div className="mt-3 space-y-2">
        <SideRow
          eyebrow="Away"
          side={sides.away}
          struck={game.status === "canceled"}
          win={awayWin}
          score={showScores && mode === "view" ? (game.awayScore ?? 0) : undefined}
          stepper={
            mode === "edit"
              ? { value: away, set: setAway, disabled: sides.unresolved, label: "Away score" }
              : undefined
          }
        />
        <SideRow
          eyebrow="Home"
          side={sides.home}
          struck={game.status === "canceled"}
          win={homeWin}
          score={showScores && mode === "view" ? (game.homeScore ?? 0) : undefined}
          stepper={
            mode === "edit"
              ? { value: home, set: setHome, disabled: sides.unresolved, label: "Home score" }
              : undefined
          }
        />
      </div>

      {mode === "edit" ? (
        <>
          <Button size="xl" className="mt-4 w-full" onClick={final} disabled={!canFinal}>
            Final
          </Button>
          {disabledText ? <p className="mt-1.5 text-center text-xs text-slate-500">{disabledText}</p> : null}
          <div className="mt-2 flex items-center justify-between gap-2">
            {/* Forfeit needs a team to name, so it waits on both sides. */}
            {sides.unresolved ? (
              <span />
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setMode("forfeit")}>
                Forfeit
              </Button>
            )}
            {/* One tap: a scheduled game has nothing stored to lose, and
                Reinstate undoes it. Never on a bracket game, and never once a
                result exists — the API wipes scores on any non-final status
                and Reinstate would not bring them back. */}
            {!bracket && !decided ? (
              <Button variant="ghost" size="sm" onClick={cancel}>
                Cancel game
              </Button>
            ) : null}
          </div>
        </>
      ) : mode === "forfeit" ? (
        <ForfeitChooser
          home={sides.home}
          away={sides.away}
          onPick={forfeit}
          onBack={() => setMode("edit")}
        />
      ) : (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 text-sm">
          {game.status === "forfeit" ? (
            <span className="text-slate-600">{forfeitLine(bundle, game)}</span>
          ) : game.status === "canceled" ? (
            <span className="text-slate-600">Canceled</span>
          ) : (
            <span />
          )}
          {game.status === "canceled" ? (
            <Button variant="secondary" size="sm" onClick={reinstate}>
              Reinstate
            </Button>
          ) : lockedBy !== null ? (
            <span className="text-xs text-slate-500">
              Gm {lockedBy} has been played — delete the bracket to redo this round
            </span>
          ) : (
            <Button variant="secondary" size="sm" onClick={change}>
              Change
            </Button>
          )}
        </div>
      )}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
    </Card>
  );
}

/** The second step of Forfeit: which team did not play. Rendered directly by the tests. */
export function ForfeitChooser({
  home,
  away,
  onPick,
  onBack,
}: {
  home: Side;
  away: Side;
  onPick: (teamId: string) => void;
  onBack: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm font-medium text-amber-900">Who forfeited?</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {[away, home].map((side) =>
          side.teamId ? (
            <Button key={side.teamId} variant="secondary" size="sm" onClick={() => onPick(side.teamId!)}>
              {side.label}
            </Button>
          ) : null,
        )}
        <Button variant="ghost" size="sm" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>
  );
}

interface StepperProps {
  value: string;
  set: (next: string) => void;
  disabled: boolean;
  /** "Home score" / "Away score" — the input's label; the buttons derive theirs. */
  label: string;
}

/** One side of the card: who, and either a stepper, the recorded score, or nothing. */
function SideRow({
  eyebrow,
  side,
  struck,
  win,
  score,
  stepper,
}: {
  eyebrow: "Away" | "Home";
  side: Side;
  struck: boolean;
  win: boolean;
  score?: number;
  stepper?: StepperProps;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{eyebrow}</p>
        <p
          className={cx(
            "truncate text-base font-semibold",
            side.teamId ? "text-navy-950" : "italic text-slate-400",
            struck && "line-through",
          )}
        >
          {side.label}
        </p>
      </div>
      {stepper ? (
        <Stepper {...stepper} />
      ) : score !== undefined ? (
        <span
          className={cx(
            "w-16 text-center font-display text-3xl font-bold tabular-nums",
            win ? "text-navy-950" : "text-slate-400",
          )}
        >
          {score}
        </span>
      ) : null}
    </div>
  );
}

const digits = (s: string): string => s.replace(/\D/g, "");

/** − / number / +, with 44px targets: the thing a scorer taps in the sun with a glove on. */
function Stepper({ value, set, disabled, label }: StepperProps) {
  const n = Number(value) || 0;
  const bump = (by: number) => set(String(Math.max(0, n + by)));
  const stepCls =
    "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-xl font-semibold leading-none text-slate-700 transition-colors hover:border-slate-400 active:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:pointer-events-none disabled:opacity-40";
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <button type="button" className={stepCls} aria-label={`${label} down`} onClick={() => bump(-1)} disabled={disabled || n === 0}>
        −
      </button>
      <Input
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label={label}
        className="h-11 w-16 px-1 py-0 text-center font-display text-3xl font-bold tabular-nums"
        value={value}
        disabled={disabled}
        onChange={(e) => set(digits(e.target.value))}
        onBlur={() => set(String(n))}
      />
      <button type="button" className={stepCls} aria-label={`${label} up`} onClick={() => bump(1)} disabled={disabled}>
        +
      </button>
    </div>
  );
}
